import { z } from 'zod';
import { sortParam } from '../sort.js';
import { nullableDate, optionalDate } from '../common.js';
import { PAYMENT_FILTERS } from './payments.js';
import { TAX_CODES } from '../finance/index.js';
/**
 * Ausgangsrechnungen (KON-150). Eine Rechnung ist zuerst ein Entwurf, den man frei ändern und löschen kann. Beim Ausstellen bekommt sie ihre Nummer, wird festgeschrieben
 * und erzeugt ihre PDF und ihr XML (E-Rechnung). Danach ändert sich nichts mehr, Korrekturen gehen nur per Stornorechnung oder Gutschrift (KON-152).
 */
export const SALES_INVOICE_KINDS = ['invoice', 'partial', 'final', 'credit_note', 'cancellation'];
export const SALES_INVOICE_KIND_LABELS = {
    invoice: 'Rechnung',
    partial: 'Anzahlungsrechnung',
    final: 'Schlussrechnung',
    credit_note: 'Gutschrift',
    cancellation: 'Stornorechnung',
};
export const SALES_INVOICE_STATUSES = ['draft', 'issued'];
export const SALES_INVOICE_STATUS_LABELS = { draft: 'Entwurf', issued: 'Ausgestellt' };
const TAX_CODE_KEYS = TAX_CODES.map((c) => c.code);
/** Einheiten aus den Produkten auf die Codes der E-Rechnung (UN/ECE Recommendation 20) abbilden. Unbekannte Einheiten gelten als „Stück“ (C62). */
export function unitCodeFor(unit) {
    const u = unit.trim().toLowerCase().replace(/\.$/, '');
    if (['std', 'h', 'stunde', 'stunden'].includes(u))
        return 'HUR';
    if (['tag', 'tage', 'd'].includes(u))
        return 'DAY';
    if (['min', 'minute', 'minuten'].includes(u))
        return 'MIN';
    if (['woche', 'wochen'].includes(u))
        return 'WEE';
    if (['monat', 'monate'].includes(u))
        return 'MON';
    if (['m', 'meter', 'lfm'].includes(u))
        return 'MTR';
    if (['m²', 'm2', 'qm'].includes(u))
        return 'MTK';
    if (['m³', 'm3', 'cbm'].includes(u))
        return 'MTQ';
    if (['km'].includes(u))
        return 'KMT';
    if (['kg'].includes(u))
        return 'KGM';
    if (['g'].includes(u))
        return 'GRM';
    if (['l', 'liter'].includes(u))
        return 'LTR';
    if (['pauschal', 'pauschale', 'psch', 'pausch'].includes(u))
        return 'LS';
    return 'C62';
}
export const salesInvoiceLineInput = z
    .object({
    title: z.string().trim().min(1, 'Bezeichnung fehlt').max(200),
    description: z.string().trim().max(2000).nullable().optional().transform((v) => (v ? v : null)),
    quantityMilli: z.number('Bitte eine Zahl angeben').int('Höchstens drei Nachkommastellen').min(1, 'Menge muss größer als 0 sein').max(100_000_000, 'Menge zu groß'),
    unit: z.string().trim().min(1, 'Einheit fehlt').max(20),
    unitPriceCents: z.number('Bitte eine Zahl angeben').int('Betrag in Cent').min(-1_000_000_000).max(1_000_000_000),
    discountBp: z.number().int().min(0, 'Rabatt mindestens 0 %').max(10000, 'Rabatt höchstens 100 %').default(0),
    taxCode: z.enum(TAX_CODE_KEYS, 'Unbekannter Steuerschlüssel'),
    /** Positionen eines Auftrags und ihre Herkunft, für die Teilabrechnung (KON-152) */
    orderLineId: z.uuid().nullable().optional(),
    itemId: z.uuid().nullable().optional(),
})
    .strict();
const lines = z.array(salesInvoiceLineInput).max(200, 'Höchstens 200 Positionen');
const text = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v ? v : null));
/** Beim Ändern: nicht angegeben bleibt unverändert (undefined), eine leere Eingabe oder null löscht den Wert */
const patchText = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null));
/** Zahlungsbedingungen einer Rechnung: Zahlungsziel in Tagen, Skonto in Basispunkten und Tagen (beides oder keines) */
const terms = {
    paymentTermsDays: z.number('Bitte eine Zahl angeben').int().min(0, 'Nicht negativ').max(365, 'Höchstens 365 Tage').optional(),
    discountTermsBp: z.number().int().min(1, 'Mehr als 0 %').max(10000).nullable().optional(),
    discountTermsDays: z.number().int().min(1, 'Mindestens 1 Tag').max(365).nullable().optional(),
};
/** Entweder aus einem Auftrag (alles wird übernommen) oder von Hand mit Kunde und Positionen */
export const createSalesInvoiceInput = z
    .object({
    orderId: z.uuid('Ungültiger Auftrag').optional(),
    contactId: z.uuid('Bitte einen Kunden wählen').optional(),
    subject: text(200),
    customerReference: text(100),
    introText: text(5000),
    closingText: text(5000),
    serviceStart: optionalDate,
    serviceEnd: optionalDate,
    discountBp: z.number().int().min(0).max(10000).optional(),
    exemptionNotes: z.record(z.string(), z.string().trim().max(500)).optional(),
    lines: lines.optional(),
    /** Die Rechnung ist schon bezahlt (zum Beispiel über Stripe oder PayPal): Sie trägt dann keinen Zahlungshinweis mit Zahlungsziel und Bankverbindung */
    alreadyPaid: z.boolean().optional(),
    ...terms,
})
    .strict()
    .superRefine((v, ctx) => {
    if (!v.orderId && !v.contactId)
        ctx.addIssue({ code: 'custom', path: ['contactId'], message: 'Bitte einen Kunden wählen' });
    if (!v.orderId && (!v.lines || v.lines.length === 0))
        ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Mindestens eine Position' });
    if ((v.discountTermsBp == null) !== (v.discountTermsDays == null))
        ctx.addIssue({ code: 'custom', path: ['discountTermsDays'], message: 'Skonto braucht Prozent und Tage, oder beides leer' });
});
export const updateSalesInvoiceInput = z
    .object({
    subject: patchText(200),
    customerReference: patchText(100),
    introText: patchText(5000),
    closingText: patchText(5000),
    serviceStart: nullableDate,
    serviceEnd: nullableDate,
    discountBp: z.number().int().min(0).max(10000).optional(),
    exemptionNotes: z.record(z.string(), z.string().trim().max(500)).optional(),
    lines: lines.optional(),
    ...terms,
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** Storno einer ausgestellten Rechnung: sie wird vollständig zurückgenommen. Der Grund steht auf der Stornorechnung. */
export const cancelInvoiceInput = z.object({ reason: z.string().trim().min(1, 'Bitte einen Grund angeben').max(1000) }).strict();
/** Gutschrift zu einer ausgestellten Rechnung. Ohne Positionen übernimmt Wirkia alle Positionen der Rechnung, die man im Entwurf kürzen kann. */
export const createCreditNoteInput = z
    .object({
    reason: text(1000),
    lines: lines.optional(),
})
    .strict();
/**
 * Anzahlungsrechnung zu einem Auftrag: entweder ein Anteil des Auftragswerts in Prozent (in Basispunkten, 3000 = 30 %) oder ein Bruttobetrag in Cent.
 * Wirkia verteilt den Betrag auf die Steuersätze des Auftrags und legt einen Entwurf an.
 */
export const createPrepaymentInput = z
    .object({
    orderId: z.uuid('Bitte einen Auftrag wählen'),
    percentBp: z.number('Bitte eine Zahl angeben').int().min(1, 'Mindestens 0,01 %').max(10000, 'Höchstens 100 %').optional(),
    amountCents: z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(1, 'Der Betrag muss größer als 0 sein').max(1_000_000_000_00).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.percentBp === undefined) === (v.amountCents === undefined))
        ctx.addIssue({ code: 'custom', path: ['percentBp'], message: 'Bitte entweder einen Prozentsatz oder einen Betrag angeben' });
});
export const listSalesInvoicesQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    status: z.enum(SALES_INVOICE_STATUSES).optional(),
    kind: z.enum(SALES_INVOICE_KINDS).optional(),
    contactId: z.uuid().optional(),
    orderId: z.uuid().optional(),
    payment: z.enum(PAYMENT_FILTERS).optional(),
    /** Nur Belege dieses Jahres (nach Rechnungsdatum, Entwürfe ohne Datum nach dem Tag der Anlage) */
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    /** Ohne Angabe: neueste zuerst, bei „offen“ und „überfällig“ nach Fälligkeit */
    sort: sortParam(['number', 'contact', 'kind', 'status', 'issueDate', 'dueDate', 'gross', 'open', 'createdAt'], '-createdAt').optional(),
    q: z.string().trim().max(100).optional(),
});
//# sourceMappingURL=invoices-out.js.map